/**
 * Two-FA Input Card
 *
 * Inline code entry for LinkedIn 2FA during account onboarding. Submitting
 * sends the code into the chat, where the assistant forwards it via
 * submit2FACode. A separate button covers the push-notification variant
 * ("approve in the LinkedIn app") which has no code.
 */

import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button } from '@mui/material';
import LockClockIcon from '@mui/icons-material/LockClock';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { TwoFaInputCardData } from '../../../types/ai-chat.types';
import { useAIChat } from '../../../hooks/use-ai-chat';

interface TwoFaInputCardProps {
  data: TwoFaInputCardData;
}

/** Counts down to `deadlineAt`; null when there is nothing to count down to. */
const useRemainingSeconds = (deadlineAt?: string): number | null => {
  const [remaining, setRemaining] = useState<number | null>(null);

  React.useEffect(() => {
    if (!deadlineAt) {
      setRemaining(null);
      return;
    }
    const target = new Date(deadlineAt).getTime();
    if (Number.isNaN(target)) {
      setRemaining(null);
      return;
    }
    const tick = () => setRemaining(Math.max(0, Math.round((target - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadlineAt]);

  return remaining;
};

const TwoFaInputCard: React.FC<TwoFaInputCardProps> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { send, isSending } = useAIChat();
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Missing flags mean an older record; assume a code exists, as this card always did.
  const awaitingApproval = data.awaitingApproval === true;
  const canRequestCode = data.canRequestCode !== false;
  const codeAvailable = data.codeAvailable !== false;
  const remainingSeconds = useRemainingSeconds(awaitingApproval ? data.approvalDeadlineAt : undefined);

  // While we are actively waiting on the app tap, a code field is not just useless —
  // it is misleading, because no code was sent. Same once the wait has expired with no
  // code route at all.
  const approvalExpiredWithoutCode = !awaitingApproval && !canRequestCode && !codeAvailable;
  const showCodeField = !awaitingApproval && !approvalExpiredWithoutCode;

  const submitCode = () => {
    const trimmed = code.trim();
    if (!trimmed || isSending) return;
    send(`2FA code for account ${data.accountId}: ${trimmed}`);
    setSubmitted(true);
  };

  const submitApproved = () => {
    if (isSending) return;
    send(`I approved the LinkedIn push notification for account ${data.accountId}. Please continue the login.`);
    setSubmitted(true);
  };

  return (
    <Paper className={classes.card}>
      <Box className={classes.cardHeader}>
        <LockClockIcon className={classes.cardIcon} />
        <Typography variant="subtitle2">
          {awaitingApproval
            ? t('twoFaApproveInApp', 'Confirm the login in your LinkedIn app')
            : t('twoFaRequired', 'Two-factor authentication required')}
        </Typography>
      </Box>

      <Typography variant="body2" color="textSecondary" className={classes.description}>
        {awaitingApproval
          ? t(
              'twoFaWaitingForPush',
              'LinkedIn sent a notification to your LinkedIn app{{forEmail}}. Open it and tap approve — the login continues by itself, no code needed.',
              { forEmail: data.email ? ` (${data.email})` : '' },
            )
          : approvalExpiredWithoutCode
            ? t(
                'twoFaApprovalExpired',
                'The confirmation did not arrive in time, and LinkedIn offers no code for this account. Ask me to connect the account again and keep your phone ready.',
              )
            : data.email
              ? t('twoFaHintWithEmail', 'LinkedIn sent a verification code for {{email}}. Enter it below, or approve the push notification in your LinkedIn app.', { email: data.email })
              : t('twoFaHint', 'LinkedIn sent a verification code. Enter it below, or approve the push notification in your LinkedIn app.')}
      </Typography>

      {remainingSeconds !== null && remainingSeconds > 0 && (
        <Typography variant="caption" className={classes.waitingHint}>
          {t('twoFaWaitingCountdown', 'Waiting for your confirmation — {{seconds}}s left', {
            seconds: remainingSeconds,
          })}
        </Typography>
      )}

      {showCodeField && (
        <Box className={classes.inputRow}>
          <TextField
            size="small"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9a-zA-Z]/g, ''))}
            placeholder={t('twoFaCodePlaceholder', 'e.g. 123456')}
            disabled={isSending || submitted}
            inputProps={{ maxLength: 10, inputMode: 'numeric' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitCode();
            }}
            className={classes.codeField}
          />
          <Button
            variant="contained"
            size="small"
            onClick={submitCode}
            disabled={!code.trim() || isSending || submitted}
          >
            {t('submitCode', 'Submit code')}
          </Button>
        </Box>
      )}

      {!approvalExpiredWithoutCode && (
        <Button
          size="small"
          variant={awaitingApproval ? 'contained' : 'text'}
          onClick={submitApproved}
          disabled={isSending || submitted}
          className={classes.approvedButton}
        >
          {t('twoFaApprovedInApp', 'I approved it in the LinkedIn app')}
        </Button>
      )}

      {submitted && (
        <Typography variant="caption" color="textSecondary">
          {t('twoFaSubmitted', 'Submitted — verification is running...')}
        </Typography>
      )}
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: '#fff',
    border: `1px solid ${theme.palette.warning.light}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  cardIcon: {
    fontSize: '1.25rem',
    color: theme.palette.warning.main,
  },
  description: {
    marginBottom: theme.spacing(1.5),
  },
  inputRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  codeField: {
    flex: 1,
    maxWidth: 180,
  },
  approvedButton: {
    marginTop: theme.spacing(1),
    textTransform: 'none',
  },
  waitingHint: {
    display: 'block',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

export default TwoFaInputCard;
